import Box from "@mui/material/Box";
import IconConstants from "../../utils/IconConstants";
import { useEffect, useState } from "react";
import { CancelRounded, Edit, Restore, Save } from "@mui/icons-material";
import {
  IconButton,
  MenuItem,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { Helper } from "../../utils/Helpers";
import { format } from "date-fns";
import RecordPaymentConfig from "../../config/forms/RecordPayment-config";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectAuthData } from "../../core/store/redux/slices/Auth-slice";
import RefundHistory from "../refund/RefundHistory";

const { getLocalTimeFromUtc, roundOff } = new Helper();

export const EditableCell = ({
  isEditing,
  value,
  onChange,
  field,
  type,
  error,
  rowIndex,
  options,
  minDate,
  maxDate,
  maxLength,
  isDisabled
}) => {
  const [localValue, setLocalValue] = useState(value || "");
  const { t } = useTranslation();

  useEffect(() => {
    setLocalValue(value || "");
  }, [value, rowIndex]);

  const handleChange = (event) => {
    const { value, type } = event.target;

    let processedValue;
    if (type === "number") {
      processedValue = Number(value) === 0 ? null : value.replace(/\D/g, "");
    } else if (type === "text") {
      processedValue = value.replace(/[^A-Za-z\s]/g, "").trimStart();
    } else {
      processedValue = value;
    }
    setLocalValue(processedValue);
    onChange(processedValue);
  };

  return isEditing ? (
    <TextField
      disabled={isDisabled}
      size="small"
      select={type === "select"}
      type={type}
      name={field}
      value={type == "select" ? options?.some((opt) => opt.payment_type_id === localValue) ? localValue : options?.[0]?.payment_type_id : localValue} 
      onChange={handleChange}
      fullWidth
      error={!!error?.[field]}
      helperText={error?.[field]}
      className="NoSpinnerTextField no-spinner"
      sx={{
        width: { xs: "30vw", sm: "fit-content", md: "fit-content" },
        minWidth: { md: "110px" },
        "& .MuiInputBase-root": {
          fontSize: "clamp(12px, 0.9vw, 15px)",
          color: "#5F5F5F",
        },
      }}
      InputProps={{
        sx: {
          fontSize: "clamp(12px, 0.9vw, 15px)",
          color: "#5F5F5F",
        },
      }}
      FormHelperTextProps={{
        sx: {
          fontSize: "clamp(10px, 0.7vw, 12px)",
          position: "absolute",
          bottom: 0,
        },
      }}
      InputLabelProps={{
        sx: { fontSize: "clamp(12px, 0.9vw, 15px)" },
      }}
      inputProps={{ min: minDate, max: maxDate, maxLength: maxLength }}
    >
      {options?.map((option) => (
        <MenuItem
          key={option.payment_type_id}
          value={option.payment_type_id}
          sx={{
            fontSize: "clamp(12px, 0.9vw, 15px)",
          }}
        >
          {t(
            `payment_mode_dropdown.${option.payment_type
              ?.toLowerCase()
              .replace(/\s+/g, "_")}`
          )}
        </MenuItem>
      ))}
    </TextField>
  ) : field === "payment_date" ? (
    value ? (
      format(value, "dd-MM-yyyy HH:mm")
    ) : (
      "-"
    )
  ) : (
    value || "-"
  );
};

const EditPaymentHistory = ({
  open,
  bookingId,
  totalPrice,
  handleClose,
  data,
  paymentTypes,
  setEditedPaymentHistory,
  setPaidAmount,
  otherInvoicesAmount,
  noPendingDues,
  checkinAmounts,
}) => {
  const { t } = useTranslation();
  const { api } = ApiClient();
  const theme = useTheme();
  const authData = useSelector(selectAuthData);
  const [editingRow, setEditingRow] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activeRowData, setActiveRowData] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [completedRefundTotal, setCompletedRefundTotal] = useState(0);
  const { created_at } = data;

  useEffect(() => {
    if (bookingId) {
      getPaymentHistory();
    }
  }, [bookingId]);

  const getPaymentHistory = async () => {
    try {
      const [paymentHistory, refundHistory] = await Promise.all([
        api
          .get(`${END_POINT.PAYMENT.GET_PAYMENT_HISTORY}${bookingId}`)
          .then((res) => res?.data?.data),
        api
          .get(END_POINT.REFUND.HISTORY(bookingId))
          .then((res) => res?.data?.data)
          .catch(() => []),
      ]);
      setCompletedRefundTotal(
        (refundHistory || [])
          ?.filter((r) => r.refund_status === "COMPLETED")
          ?.reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0)
      );
      if (paymentHistory) {
        const payments = paymentHistory?.map((p) => ({
          ...p,
          payment_date: getLocalTimeFromUtc(p.payment_date)?.dateTimeString,
        }));

        setPaymentHistory(payments);
      }
    } catch (error) {}
  };

  const paidAmount = paymentHistory
    ?.filter((p) => p.is_deleted != 1)
    ?.reduce((sum, p) => sum + (Number(p.paid_amount) || 0), 0) -
    completedRefundTotal;

  useEffect(() => {
    setPaidAmount(paidAmount);
  }, [paidAmount]);

  const handleCloseModal = () => {
    handleClose();
    setEditedPaymentHistory(paymentHistory);
    setEditingRow(null);
    setRowErrors({});
  };

  const handleEditClick = (rowIndex, row) => {
    setRowErrors({});
    setEditingRow(rowIndex);
    setEditableData(row);
    setActiveRowData(row);
  };

  const handleSaveClick = async (payment_id) => {
    const isValid = await validateRow(editableData);
    if (!isValid) return;

    setPaymentHistory((prev) =>
      prev.map((item) =>
        item.payment_id === payment_id ? { ...item, ...editableData } : item
      )
    );
    setEditingRow(null);
  };

  const handleCancelClick = () => {
    setEditableData(activeRowData);
    setEditingRow(null);
    setRowErrors({});
  };

  const handleDeleteClick = (payment_id) => {
    setPaymentHistory((prev) =>
      prev.map((item) =>
        item.payment_id === payment_id ? { ...item, is_deleted: true } : item
      )
    );
  };

  const handleRestoreClick = (payment_id) => {
    setPaymentHistory((prev) =>
      prev.map((item) =>
        item.payment_id === payment_id ? { ...item, is_deleted: false } : item
      )
    );
  };

  const validateRow = async (row) => {
    try {
      await RecordPaymentConfig(
        null,
        t("common_components.payments.required")
      ).validate(row, {
        abortEarly: false,
      });
      return true;
    } catch (err) {
      const errors = {};
      err?.inner?.forEach((error) => {
        errors[error.path] = error.message;
      });
      setRowErrors((prevErrors) => ({
        ...prevErrors,
        ...errors,
      }));

      return false;
    }
  };

  const renderRows = () =>
    paymentHistory?.map((row, rowIndex) => {
      return (
        <TableRow
          key={rowIndex}
          sx={{
            "& .MuiTableCell-root": {
              fontSize: "clamp(12px, 0.9vw, 15px)",
              whiteSpace: "nowrap",
              color: "#5F5F5F",
              paddingY: "0.7%",
            },
            backgroundColor: row.is_deleted
              ? theme.palette.grey[100]
              : "inherit",
          }}
        >
          <TableCell align="center">{rowIndex + 1}</TableCell>
          <TableCell>
            <EditableCell
              isEditing={editingRow === rowIndex}
              value={row.payment_date}
              minDate={getLocalTimeFromUtc(created_at)?.dateTimeString}
              maxDate={format(new Date(), "yyyy-MM-dd HH:mm")}
              onChange={(value) => {
                setRowErrors((prevErrors) => ({
                  ...prevErrors,
                  payment_date: "",
                }));
                setEditableData((prev) => ({ ...prev, payment_date: value }));
              }}
              rowIndex={editingRow}
              error={rowErrors}
              field="payment_date"
              type="datetime-local"
            />
          </TableCell>
          <TableCell>
            <EditableCell
              isEditing={editingRow === rowIndex}
              value={Number(row.paid_amount)}
              onChange={(value) => {
                setRowErrors((prevErrors) => ({
                  ...prevErrors,
                  paid_amount: "",
                }));
                setEditableData((prev) => ({ ...prev, paid_amount: value }));
              }}
              rowIndex={editingRow}
              error={rowErrors}
              field="paid_amount"
              type="number"
              maxLength={50}
            />
          </TableCell>

          <TableCell>
            <EditableCell
              isEditing={editingRow === rowIndex}
              value={
                editingRow === rowIndex
                  ? row?.payment_type_id
                  : t(
                      `payment_mode_dropdown.${row?.payment_type
                        ?.toLowerCase()
                        .replace(/\s+/g, "_")}`
                    )
              }
              onChange={(value) => {
                setRowErrors((prevErrors) => ({
                  ...prevErrors,
                  payment_type_id: "",
                }));
                setEditableData((prev) => ({
                  ...prev,
                  payment_type_id: value,
                  payment_type: paymentTypes.find(
                    (v) => v.payment_type_id === value
                  )?.payment_type,
                }));
              }}
              rowIndex={editingRow}
              options={paymentTypes}
              error={rowErrors}
              field="payment_type_id"
              type="select"
            />
          </TableCell>

          <TableCell>
            <EditableCell
              isEditing={editingRow === rowIndex}
              value={row.remarks}
              onChange={(value) => {
                setRowErrors((prevErrors) => ({
                  ...prevErrors,
                  remarks: "",
                }));
                setEditableData((prev) => ({
                  ...prev,
                  remarks: value,
                }));
              }}
              rowIndex={editingRow}
              error={rowErrors}
              field="remarks"
              type="text"
              maxLength={50}
            />
          </TableCell>

          <TableCell align="center">
            {editingRow === rowIndex ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleSaveClick(row.payment_id)}
                >
                  <Save
                    sx={{
                      height: "clamp(18px, 1.4vw, 22px)",
                      width: "clamp(18px, 1.4vw, 22px)",
                    }}
                  />
                </IconButton>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleCancelClick}
                >
                  <CancelRounded
                    sx={{
                      height: "clamp(18px, 1.4vw, 22px)",
                      width: "clamp(18px, 1.4vw, 22px)",
                    }}
                  />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {row.is_deleted ? (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleRestoreClick(row.payment_id)}
                  >
                    <Restore
                      sx={{
                        height: "clamp(18px, 1.4vw, 22px)",
                        width: "clamp(18px, 1.4vw, 22px)",
                      }}
                    />
                  </IconButton>
                ) : (
                  <>
                    <IconButton
                      onClick={() => handleEditClick(rowIndex, row)}
                      size="small"
                      color="primary"
                    >
                      <Edit
                        fontSize="small"
                        sx={{
                          height: "clamp(18px, 1.4vw, 22px)",
                          width: "clamp(18px, 1.4vw, 22px)",
                        }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(row.payment_id)}
                      size="small"
                      color="primary"
                    >
                      <Box
                        sx={{
                          height: "clamp(18px, 1.4vw, 22px)",
                          width: "clamp(18px, 1.4vw, 22px)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          img: {
                            width: "100%",
                            height: "100%",
                          },
                        }}
                      >
                        <img src={IconConstants.GIDelete} alt="" />
                      </Box>
                    </IconButton>
                  </>
                )}
              </Box>
            )}
          </TableCell>
        </TableRow>
      );
    });

  return (
    <>
      <Modal open={open} onClose={handleCloseModal}>
        <Box
          sx={{
            margin: "auto",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            bgcolor: "background.paper",
            borderRadius: "16px",
            width: { xs: "94%", sm: "92%", md: "80vw" },
            maxWidth: "1080px",
            minWidth: { md: "640px" },
            height: "auto",
            maxHeight: "92vh",
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: "3% 0", md: "1% 0" },
          }}
        >
          <Box sx={{ m: "1% 0 2% 0", width: 1, flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: "clamp(16px, 1.15vw, 21px)",
                color: "#252525",
                fontWeight: 600,
                margin: "0 0 1% 3%",
              }}
            >
              {t("common_components.payments.payment_history")}
            </Typography>
          </Box>

          <TableContainer
            sx={{
              width: 1,
              maxHeight: { xs: "38vh", md: "34vh" },
              overflow: "auto",
            }}
          >
            <Table size="small" sx={{ width: 1 }}>
              <TableHead
                sx={{
                  background: theme.palette.tab.gradient,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  width: 1,
                }}
              >
                <TableRow sx={{ textWrap: "nowrap" }}>
                  {[
                    "Serial Number",
                    "Date",
                    "Amount Paid",
                    "Payment Mode",
                    "Remarks",
                    "Action",
                  ].map((label, index) => (
                    <TableCell
                      key={index}
                      align={
                        label === "SI No" || label === "Action"
                          ? "center"
                          : "left"
                      }
                      sx={{
                        fontWeight: 600,
                        color: "#FFFFFF",
                        fontSize: "clamp(12px, 0.9vw, 15px)",
                      }}
                    >
                      {t(
                        `common_components.payments.${label
                          .toLowerCase()
                          .replace(/\s+/g, "_")}`
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              rowGap: "8px",
              columnGap: "16px",
              margin: "2% auto",
              width: "95%",
            }}
          >
            <Box sx={{ width: { xs: "auto", sm: "auto" } }}>
              <Typography
                sx={{
                  fontSize: "clamp(13px, 1.05vw, 17px)",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {`${t("common_components.payments.total_price")}: ₹ ${
                  totalPrice + otherInvoicesAmount + checkinAmounts
                }`}
              </Typography>
            </Box>
            <Box sx={{ width: "auto" }}>
              <Typography
                sx={{
                  fontSize: "clamp(13px, 1.05vw, 17px)",
                  fontWeight: 500,
                  color: noPendingDues ? "#62BA60" : "#DB563F",
                  whiteSpace: "nowrap",
                }}
              >
                {`${t(
                  "common_components.payments.paid_amount"
                )}: ₹ ${paidAmount}`}
              </Typography>
            </Box>
            <Box sx={{ width: "auto" }}>
              <Typography
                sx={{
                  fontSize: "clamp(13px, 1.05vw, 17px)",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {`${t("common_components.payments.invoice_price")}: ₹ ${
                  totalPrice || "N/A"
                }`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: "2.5%", pb: "2%" }}>
            <RefundHistory
              bookingId={bookingId}
              timezone={authData?.timezone}
              onPrintReceipt={() => {}}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default EditPaymentHistory;
